/**
 * FilterDropdown Component
 *
 * Local replacement for SearchableMultiSelectDropdown with configurable width.
 * Provides multi-select filtering with search, group support, and apply/clear actions.
 *
 * Key Features:
 * - Configurable dropdown width (fixes text truncation issues)
 * - Enhanced options logic (preserves selected values not in current options)
 * - Apply button state management (only enabled when changes made)
 * - Search filtering with selected items always visible
 * - Group selection with tri-state checkboxes
 * - Virtualization for large option lists
 * - Portal rendering with smart positioning
 * - Count-only display: Shows numeric count of selected items (e.g., "3") instead of labels
 * - Always-visible tooltip: Hover over count to see full list of selected items
 *
 * Display Pattern:
 * - Button shows: [Filter Label | 3 ▼]
 * - Tooltip shows full list of selected item labels on hover
 * - No label truncation needed - count is always compact
 *
 * Width Control Implementation:
 * - Width prop passed directly to DropdownList as inline style (line 514)
 * - Cannot use parent wrapper width due to Tailwind class specificity (w-[280px]/w-[400px])
 * - Inline style on DropdownList overrides internal Tailwind width classes
 * - fitWidth={true} makes option text expand, width prop controls dropdown container
 */

import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { PlusCircleIcon, XCircleIcon } from '@heroicons/react/24/solid';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { twMerge } from 'tailwind-merge';

import { Loader } from '@/components/Loader';
import { Tooltip } from '@/components/Tooltip';
import { DropdownList } from '@/components/ui/DropdownList';

import { FilterDropdownProps, FilterOption, GroupOption } from './types';
import {
  enforceMaxSelections,
  flattenOptions,
  isGroupOption,
  isValidMaxSelections,
  toggleGroupOption,
  toggleIndividualOption,
} from './utils';

export const FilterDropdown: React.FC<FilterDropdownProps> = ({
  id,
  label,
  options,
  selectedValues,
  onApply,
  onClear,
  isLoadingOptions = false,
  isPraetorianUser = false,
  onDropdownStateChange,
  width = 400, // Default to existing 400px width
  isLoading = false,
  maxSelections,
}) => {
  // State management
  const [isOpen, setIsOpen] = useState(false);
  const [tempSelected, setTempSelected] = useState<FilterOption[]>([]);
  const [searchValue, setSearchValue] = useState('');
  const [buttonPosition, setButtonPosition] = useState<{
    bottom: number;
    left: number;
    width: number;
  }>();

  // Refs
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Runtime validation of maxSelections prop
  useEffect(() => {
    if (!isValidMaxSelections(maxSelections)) {
      console.error(
        `FilterDropdown [${id}]: maxSelections must be a positive integer, got:`,
        maxSelections
      );
    }
  }, [maxSelections, id]);

  /**
   * Reset search when dropdown closes to ensure fresh state on reopen
   */
  useEffect(() => {
    if (!isOpen) {
      setSearchValue('');
    }
  }, [isOpen]);

  /**
   * Helper function to find an option by value in the options structure
   * Searches both flat options and nested group items
   */
  const findOptionByValue = useCallback(
    (
      value: string,
      opts: (FilterOption | GroupOption)[]
    ): FilterOption | undefined => {
      for (const opt of opts) {
        if (opt.value === value) return opt;
        if ((opt as GroupOption).isGroup && (opt as GroupOption).items) {
          const found = findOptionByValue(value, (opt as GroupOption).items!);
          if (found) return found;
        }
      }
      return undefined;
    },
    []
  );

  /**
   * Enhanced Options Logic
   *
   * Critical feature: Include previously selected values that may not be in current options.
   * This prevents selected items from disappearing when API returns new filter options.
   *
   * Example: User selects "Amazon Registrar, Inc." → API updates options → "Amazon Registrar, Inc."
   * is no longer in the list but should still be visible as selected.
   *
   * In single-select mode (maxSelections === 1), flattens grouped options to remove group headers.
   */
  const enhancedOptions = useMemo(() => {
    const selectedOptionsNotInCurrent: FilterOption[] = [];

    // Find selected values that don't exist in current options
    for (const value of selectedValues) {
      const existsInCurrent = findOptionByValue(value, options);
      if (!existsInCurrent) {
        selectedOptionsNotInCurrent.push({
          id: value,
          label: value,
          value: value,
        });
      }
    }

    // Combine current options with any missing selected options
    let allOptions = [...options, ...selectedOptionsNotInCurrent];

    // Flatten groups in single-select mode (maxSelections === 1)
    if (maxSelections === 1) {
      allOptions = flattenOptions(allOptions);
    }

    return allOptions;
  }, [options, selectedValues, findOptionByValue, maxSelections]);

  /**
   * Search Filtering Logic
   *
   * - Filters options based on search value
   * - Selected items always visible (even if they don't match search)
   * - Groups visible if any child matches search OR group label matches
   * - Groups with selected items always visible
   */
  const filteredOptions = useMemo(() => {
    if (!searchValue) return enhancedOptions;

    // Create a set of selected values for quick lookup
    const selectedValuesSet = new Set(selectedValues);

    return enhancedOptions
      .map(option => {
        if ((option as GroupOption).isGroup && (option as GroupOption).items) {
          const groupOption = option as GroupOption;
          // Filter items in the group, but keep selected items regardless of search
          const filteredItems = groupOption.items!.filter(
            item =>
              item.label.toLowerCase().includes(searchValue.toLowerCase()) ||
              (item.value && selectedValuesSet.has(item.value.toString()))
          );

          // Return group with filtered items
          return {
            ...groupOption,
            items: filteredItems,
          } as GroupOption;
        }
        // For non-group options, just return as-is
        return option;
      })
      .filter(option => {
        if ((option as GroupOption).isGroup && (option as GroupOption).items) {
          const groupOption = option as GroupOption;
          // Keep group if it has matching items or its own label matches
          return (
            groupOption.items!.length > 0 ||
            option.label.toLowerCase().includes(searchValue.toLowerCase())
          );
        }
        // Keep non-group option if its label matches, it's selected, or it's a previously selected option
        return (
          option.label.toLowerCase().includes(searchValue.toLowerCase()) ||
          (option.value && selectedValuesSet.has(option.value.toString()))
        );
      });
  }, [enhancedOptions, searchValue, selectedValues]);

  /**
   * Selected Options Mapping
   *
   * Maps selectedValues (string[]) to FilterOption[] for display and processing
   * Deduplicates to avoid showing duplicate options
   */
  const selected = useMemo(() => {
    const selectedOptions: FilterOption[] = [];

    // Deduplicate selectedValues
    const uniqueSelectedValues = [...new Set(selectedValues)];

    // Process each selected value - all should be found in enhancedOptions
    for (const value of uniqueSelectedValues) {
      const option = findOptionByValue(value, enhancedOptions);
      if (option) {
        selectedOptions.push(option);
      }
    }

    return selectedOptions;
  }, [enhancedOptions, selectedValues, findOptionByValue]);

  /**
   * Dropdown Positioning with Configurable Width
   *
   * Uses the width prop for positioning calculations and dropdown sizing.
   * Prevents overflow on right/left edges of viewport.
   */
  const dropdownStyle = useMemo(() => {
    if (!isOpen || !buttonPosition) return undefined;

    const dropdownWidth = width; // Use prop directly
    const { bottom, left, width: buttonWidth } = buttonPosition;
    const screenWidth = window.innerWidth;

    // Calculate left position with overflow prevention
    let calculatedLeft = left;
    if (left + dropdownWidth > screenWidth) {
      // Try aligning dropdown's right edge with button's right edge
      calculatedLeft = left + buttonWidth - dropdownWidth;
      if (calculatedLeft < 0) {
        // Position dropdown's right edge with screen's right edge
        calculatedLeft = Math.max(0, screenWidth - dropdownWidth);
      }
    }
    // Final clamp: ensure dropdown is not off-screen to the left
    calculatedLeft = Math.max(0, calculatedLeft);

    return {
      top: `${bottom + 8}px`,
      left: `${calculatedLeft}px`,
      width: `${dropdownWidth}px`,
      pointerEvents: 'auto' as const,
    };
  }, [isOpen, buttonPosition, width]);

  /**
   * Update button position when dropdown opens or window resizes/scrolls
   */
  useEffect(() => {
    const updatePosition = () => {
      if (buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        setButtonPosition({
          bottom: rect.bottom, // Use viewport-relative coordinate
          left: rect.left, // Use viewport-relative coordinate
          width: rect.width,
        });
      }
    };

    if (isOpen) {
      updatePosition();
      window.addEventListener('resize', updatePosition);
      window.addEventListener('scroll', updatePosition, true);

      return () => {
        window.removeEventListener('resize', updatePosition);
        window.removeEventListener('scroll', updatePosition, true);
      };
    } else {
      setButtonPosition(undefined);
    }
  }, [isOpen]);

  /**
   * Handle click outside to close dropdown
   * Reverts tempSelected to original selected state (cancels changes)
   */
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        // Revert to original selection (cancel changes)
        setTempSelected(selected);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside, true);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside, true);
    };
  }, [isOpen, selected]);

  /**
   * Update temporary selection when dropdown opens
   */
  useEffect(() => {
    if (isOpen) {
      setTempSelected(selected);
    }
    // Notify parent about dropdown state change
    if (onDropdownStateChange) {
      onDropdownStateChange(isOpen, id);
    }
  }, [isOpen, selected, onDropdownStateChange, id]);

  /**
   * Handle checkbox toggle for individual items or groups.
   *
   * Enforces maxSelections constraint after selection changes.
   */
  const handleToggleCheckbox = useCallback(
    (option: FilterOption) => {
      setTempSelected(prev => {
        let newSelections: FilterOption[];

        // Handle group options
        if (isGroupOption(option)) {
          newSelections = toggleGroupOption(prev, option);
        } else {
          // Handle individual options
          newSelections = toggleIndividualOption(prev, option);
        }

        // Enforce maxSelections constraint
        return enforceMaxSelections(newSelections, maxSelections);
      });
    },
    [maxSelections]
  );

  /**
   * Handle Apply button click
   * Filters out any group items before calling onApply
   */
  const handleApply = useCallback(() => {
    // Filter out any group items before applying
    const filteredSelection = tempSelected.filter(
      item => !(item as GroupOption).isGroup
    );

    onApply(filteredSelection);
    setIsOpen(false);
  }, [tempSelected, onApply]);

  /**
   * Handle Clear/X button click
   */
  const handleClearClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      setTempSelected([]);
      onClear();
    },
    [onClear]
  );

  /**
   * Handle Select All button click
   * Selects all non-disabled, non-group options
   */
  const handleSelectAll = useCallback(() => {
    // Get all selectable options (non-group, non-disabled)
    const allSelectableOptions = enhancedOptions
      .flatMap(option => {
        if ((option as GroupOption).isGroup && (option as GroupOption).items) {
          return (option as GroupOption).items!.filter(item => !item.disabled);
        }
        return option.disabled ? [] : [option];
      })
      .filter(option => !(option as GroupOption).isGroup);

    setTempSelected(allSelectableOptions as FilterOption[]);
  }, [enhancedOptions]);

  /**
   * Handle Deselect All button click
   * Clears all selections
   */
  const handleDeselectAll = useCallback(() => {
    setTempSelected([]);
  }, []);

  /**
   * Handle Enter key press in search input - Power User Keyboard Shortcut
   *
   * When the user types in the search box and filters down to exactly ONE
   * selectable option, pressing Enter will:
   * 1. Toggle that option's selection state (select if unselected, deselect if selected)
   * 2. Automatically apply the change
   * 3. Close the dropdown
   *
   * This provides a fast keyboard-driven workflow for power users who want to
   * quickly filter and select options without reaching for the mouse.
   *
   * Edge cases handled:
   * - Disabled options are excluded from count
   * - Group headers are excluded from count
   * - "Select All" option is excluded from count
   * - If 0 matches or 2+ matches: no action taken
   * - Works for both selecting and deselecting options
   *
   * @param e - Keyboard event from search input
   */
  const handleSearchKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key !== 'Enter') return;

      e.preventDefault();
      e.stopPropagation();

      // Extract all selectable options (non-group, non-disabled, non-SelectAll)
      const selectableOptions = filteredOptions
        .flatMap(option => {
          if (
            (option as GroupOption).isGroup &&
            (option as GroupOption).items
          ) {
            return (option as GroupOption).items || [];
          }
          return [option];
        })
        .filter(opt => {
          // Exclude disabled options
          if (opt.disabled) return false;

          // Exclude groups
          if ((opt as GroupOption).isGroup) return false;

          // Exclude "Select All" option - it's not a real selectable filter value
          // Note: "Select All" is not in filteredOptions since it's added by DropdownList,
          // but we check just to be safe
          if (opt.value === '__select_all__') return false;
          if (opt.label === 'Select All') return false;

          return true;
        });

      // Only proceed if exactly ONE selectable option
      if (selectableOptions.length !== 1) return;

      const singleOption = selectableOptions[0];

      // Toggle selection state
      const isCurrentlySelected = tempSelected.some(
        item => item.value === singleOption.value
      );

      const newSelection = isCurrentlySelected
        ? tempSelected.filter(item => item.value !== singleOption.value)
        : [...tempSelected, singleOption];

      // Update temp selection and apply immediately
      setTempSelected(newSelection);

      // Filter out any group items before applying (same as handleApply)
      const filteredSelection = newSelection.filter(
        item => !(item as GroupOption).isGroup
      );

      onApply(filteredSelection);
      setIsOpen(false);
    },
    [filteredOptions, tempSelected, onApply]
  );

  /**
   * Render selected content for button
   * Shows only numeric count with tooltip containing full list of selected items
   */
  const renderSelectedContent = () => {
    const active = selected.length > 0;
    if (!active) return null;

    // Calculate count of selected items (excluding groups)
    const count = selected.filter(
      item => !(item as GroupOption).isGroup
    ).length;
    const selectedText = count.toString();

    // Build tooltip content with full list of selected items
    const tooltipContent = (
      <div className="w-full min-w-[200px]">
        <ul className="space-y-1">
          {selected
            .filter(item => !(item as GroupOption).isGroup)
            .map((option, index) => (
              <li key={option.id ?? index} className="text-sm">
                {option.label}
              </li>
            ))}
        </ul>
      </div>
    );

    return (
      <>
        <span className="text-chariot-brand-primary mx-2">|</span>
        <Tooltip title={tooltipContent} placement="bottom">
          <span className="bg-chariot-brand-primary inline-flex h-5 w-5 items-center justify-center rounded-full text-[10px] leading-none font-bold text-white">
            {selectedText}
          </span>
        </Tooltip>
        <ChevronDownIcon className="ml-2 h-4 w-4 shrink-0 text-current" />
      </>
    );
  };

  /**
   * Process options to add checked state for DropdownList
   */
  const processedOptions = useMemo(() => {
    return filteredOptions.map(option => {
      if ((option as GroupOption).isGroup && (option as GroupOption).items) {
        const groupOption = option as GroupOption;
        // For groups, check if any items are selected
        const hasSelectedItems = groupOption.items!.some(item =>
          tempSelected.some(s => s.id === item.id)
        );

        return {
          ...groupOption,
          checked: hasSelectedItems,
          items: groupOption.items!.map(item => ({
            ...item,
            checked: tempSelected.some(s => s.id === item.id),
          })),
        };
      }

      return {
        ...option,
        checked: tempSelected.some(s => s.id === option.id),
      };
    });
  }, [filteredOptions, tempSelected]);

  const active = selected.length > 0;

  return (
    <>
      <div className="relative">
        <Loader
          className="absolute top-0 left-0 z-10 size-full rounded-2xl"
          isLoading={isLoading}
        />

        {/* Trigger Button */}
        <button
          ref={buttonRef}
          onClick={e => {
            e.stopPropagation();
            setIsOpen(!isOpen);
          }}
          data-testid={`filter-dropdown-${id}`}
          aria-label={
            active
              ? `${label}: ${selected.filter(item => !(item as GroupOption).isGroup).length} selected. ${selected
                  .filter(item => !(item as GroupOption).isGroup)
                  .map(item => item.label)
                  .join(', ')}`
              : label
          }
          className={twMerge(
            'flex max-w-full min-w-0 items-center rounded-full border border-dashed px-2 py-1 text-xs font-bold transition-colors duration-150',
            active
              ? 'border-chariot-brand-primary bg-chariot-background-secondary text-chariot-text-primary'
              : 'border-chariot-stroke text-chariot-text-secondary'
          )}
        >
          {active ? (
            <XCircleIcon
              className="text-chariot-text-primary mr-2 h-5 w-5 shrink-0"
              onClick={handleClearClick}
            />
          ) : (
            <PlusCircleIcon className="text-chariot-text-secondary mr-2 h-5 w-5 shrink-0" />
          )}
          <span className="truncate">{label}</span>
          {renderSelectedContent()}
        </button>
      </div>

      {/* Portal Dropdown */}
      {isOpen && dropdownStyle && (
        <div
          className="fixed top-0 left-0 z-9999 h-0 w-full overflow-visible"
          style={{ pointerEvents: 'none' }}
        >
          {/* Click-outside overlay */}
          <div
            className="fixed inset-0 z-40"
            style={{ pointerEvents: 'auto' }}
            onClick={() => {
              setIsOpen(false);
              // Revert to original selection (cancel changes)
              setTempSelected(selected);
            }}
          />
          {/* Dropdown content */}
          <div
            ref={dropdownRef}
            className="absolute z-50"
            style={{
              top: dropdownStyle?.top,
              left: dropdownStyle?.left,
              pointerEvents: dropdownStyle?.pointerEvents,
            }}
          >
            <DropdownList
              fitWidth={true} // Allows option text to expand fully without truncation
              options={processedOptions}
              onToggleCheckbox={handleToggleCheckbox}
              className="bg-chariot-background-primary border-chariot-brand-primary mt-2 rounded border shadow-lg"
              style={{ width: `${width}px` }} // CRITICAL: Inline style overrides Tailwind w-[280px]/w-[400px] classes
              title={`Select ${label}`}
              searchValue={searchValue}
              onSearchChange={setSearchValue}
              onSearchKeyDown={handleSearchKeyDown}
              searchPlaceholder="Search ..."
              multiSelect={true}
              onApply={handleApply}
              virtualized={filteredOptions.length > 10}
              isPraetorianUser={isPraetorianUser}
              isLoadingOptions={isLoadingOptions}
              onSelectAll={maxSelections === 1 ? undefined : handleSelectAll}
              onDeselectAll={handleDeselectAll}
            />
          </div>
        </div>
      )}
    </>
  );
};
